"use client"

import { useRef } from "react"
import { BaseInlineInput } from "@ui/forms/inline"
import { Paperclip, Plus, X } from "lucide-react"
import { toast } from "sonner"

import { DriveAttachmentButton } from "./drive-attachment-button"
import {
  addNoteAttachmentFiles,
  ALLOWED_NOTE_ATTACHMENT_TYPES,
  MAX_NOTE_ATTACHMENTS,
  type DraftAttachment,
} from "./note-attachment-files"

export type { DraftAttachment } from "./note-attachment-files"

interface NoteAttachmentsProps {
  attachments: DraftAttachment[]
  onChange: (next: DraftAttachment[]) => void
  disabled?: boolean
  // Connected provider folder for the record this note hangs off (household, contact,
  // or business). The drive picker opens here rather than at the drive root.
  storageFolderId?: string
  storageFolderName?: string
}

export function NoteAttachments({
  attachments,
  onChange,
  disabled,
  storageFolderId,
  storageFolderName,
}: NoteAttachmentsProps) {
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? [])
    if (files.length === 0) return

    const result = addNoteAttachmentFiles(attachments, files)
    if (!result.ok) {
      toast.error(result.error)
      return
    }

    onChange(result.attachments)
    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  const handleAddLink = (link: { url: string; name: string }) => {
    onChange([...attachments, { kind: "link", url: link.url, name: link.name }])
  }

  const handleRemove = (index: number) => {
    onChange(attachments.filter((_, i) => i !== index))
  }

  return (
    <BaseInlineInput label="Attachments" LabelIcon={Paperclip} fixedLabelWidth="120px" alignLabel="start">
      <div className="flex w-full flex-col gap-2">
        {attachments.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {attachments.map((attachment, index) => {
              const label =
                attachment.kind === "link"
                  ? attachment.name
                  : attachment.kind === "existing-file"
                    ? attachment.fileName
                    : attachment.file.name
              const href =
                attachment.kind === "link"
                  ? attachment.url
                  : attachment.kind === "existing-file"
                    ? attachment.downloadUrl
                    : undefined

              return (
                <div key={index} className="flex h-7 items-center gap-1 rounded-md border px-2 text-xs">
                  <Paperclip className="size-3" />
                  {href ? (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="max-w-[140px] truncate hover:underline"
                    >
                      {label}
                    </a>
                  ) : (
                    <span className="max-w-[140px] truncate">{label}</span>
                  )}
                  {!disabled && (
                    <button
                      type="button"
                      onClick={() => handleRemove(index)}
                      className="text-icon hover:text-destructive ml-0.5 rounded-sm"
                    >
                      <X className="size-3" />
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {!disabled && attachments.length < MAX_NOTE_ATTACHMENTS && (
          <div className="flex items-center gap-1">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ALLOWED_NOTE_ATTACHMENT_TYPES.join(",")}
              onChange={handleFileSelect}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="text-muted-foreground hover:bg-muted hover:text-foreground focus:outline-primary flex h-8 cursor-pointer items-center gap-1.5 rounded-md px-2 text-sm focus:outline-1"
            >
              <Plus className="size-3.5" />
              <span>Upload file</span>
            </button>
            <DriveAttachmentButton
              onAttach={handleAddLink}
              rootFolderId={storageFolderId}
              rootFolderName={storageFolderName}
            />
          </div>
        )}
      </div>
    </BaseInlineInput>
  )
}
